import { useState, useEffect, useContext, useRef, useCallback } from "react";
import { Outlet } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Header from "@components/Layout/Header";
import Sidebar from "@components/Layout/Sidebar";
import UpdateSummaryModal from "@components/Dialogs/UpdateSummaryModal";
import FullScreenDotLoader from "@components/UI/FullScreenDotLoader";
import useUpdateSummary from "@hooks/useUpdateSummary";
import useCrossTabAuth from "@hooks/useCrossTabAuth";
import useAuthorizationWatcher, { arePermissionsEqual } from "@hooks/useAuthorizationWatcher";
import useInactivityTimer from "@hooks/useInactivityTimer";
import { useAutosyncAllDataAndStore } from "@hooks/useAppDataLoader";
import { PermissionContext } from "@contexts/PermissionContext";
import { connectSSE, disconnectSSE } from "@services/sseService";
import { deleteDataInIndexedDb, deleteProjectCascadeInIndexedDb, updateDataInIndexedDb, updateLocalPreferences } from "@utils/indexedDb";
import { STORES } from "@utils/indexedDb";
import { showSuccessToast, showErrorToast, showWarningToast } from "@configs/react-toastify/snackbar";
import { getLocalStorage } from "@utils/local-storage";
import { LOCAL_STORAGE } from "@constants/index";
import { forceLogoutByAccountInactive, forceLogoutByAuthorizationChange, forceLogoutByPasswordReset } from "@utils/auth";

const dispatchDataSyncComplete = () => {
  window.dispatchEvent(
    new CustomEvent("dataSyncComplete", {
      detail: { syncTime: Date.now(), source: "sse" },
    }),
  );
};

const normalizeGenesisSseEntities = (entityField) => {
  if (!entityField) {
    return ["components", "licenses"];
  }
  return Array.isArray(entityField) ? entityField : [entityField];
};

const dispatchGenesisImportComplete = (entities) => {
  window.dispatchEvent(
    new CustomEvent("genesis_import_complete", { detail: { entities } })
  );
};

const getGenesisImportToastContent = (t, entities, importCount, insertedCount, updatedCount, isPartial) => {
  // v2 SSE: one entity per stream. v1 combined: ["components", "licenses"].
  const entity = entities.length === 1 ? entities[0] : null;
  const isComponentsAndLicenses =
    entities.length === 2 &&
    entities.includes("components") &&
    entities.includes("licenses");

  if (entity === "components") {
    return {
      title: t(isPartial
        ? "preferences.genesis-sync-components-partial-success"
        : "preferences.genesis-sync-components-success"),
      body: t("preferences.genesis-sync-count-components", {
        processedComponentCount: importCount?.component || 0,
        insertComponentCount: insertedCount?.component || 0,
        updatedComponentCount: updatedCount?.component || 0,
      }),
    };
  }

  if (entity === "licenses") {
    return {
      title: t(isPartial
        ? "preferences.genesis-sync-licenses-partial-success"
        : "preferences.genesis-sync-licenses-success"),
      body: t("preferences.genesis-sync-count-licenses", {
        processedLicenseCount: importCount?.license || 0,
        insertLicenseCount: insertedCount?.license || 0,
        updatedLicenseCount: updatedCount?.license || 0,
      }),
    };
  }

  if (entity === "license_attributes") {
    return {
      title: t(isPartial
        ? "preferences.genesis-sync-license-attributes-partial-success"
        : "preferences.genesis-sync-license-attributes-success"),
      body: t("preferences.genesis-sync-count-license-attributes", {
        processedLicenseAttributeCount: importCount?.licenseAttribute || 0,
        insertLicenseAttributeCount: insertedCount?.licenseAttribute || 0,
        updatedLicenseAttributeCount: updatedCount?.licenseAttribute || 0,
      }),
    };
  }

  if (entity === "license_periods") {
    return {
      title: t(isPartial
        ? "preferences.genesis-sync-license-periods-partial-success"
        : "preferences.genesis-sync-license-periods-success"),
      body: t("preferences.genesis-sync-count-license-periods", {
        processedLicensePeriodCount: importCount?.licensePeriod || 0,
        insertLicensePeriodCount: insertedCount?.licensePeriod || 0,
        updatedLicensePeriodCount: updatedCount?.licensePeriod || 0,
      }),
    };
  }

  if (entity === "cves") {
    return {
      title: t(isPartial
        ? "preferences.genesis-sync-cves-partial-success"
        : "preferences.genesis-sync-cves-success"),
      body: t("preferences.genesis-sync-count-cves", {
        processedCveCount: importCount?.cve || 0,
        insertCveCount: insertedCount?.cve || 0,
        updatedCveCount: updatedCount?.cve || 0,
      }),
    };
  }

  if (entity === "security_alerts") {
    return {
      title: t(isPartial
        ? "preferences.genesis-sync-security-alerts-partial-success"
        : "preferences.genesis-sync-security-alerts-success",
        isPartial
          ? "Genesis security alerts synchronization completed with errors!"
          : "Genesis security alerts synchronization completed successfully!"),
      body: t("preferences.genesis-sync-count-security-alerts", {
        processedSecurityAlertCount: importCount?.securityAlert || 0,
        insertSecurityAlertCount: insertedCount?.securityAlert || 0,
        updatedSecurityAlertCount: updatedCount?.securityAlert || 0,
        defaultValue:
          "Processed {{processedSecurityAlertCount}} security alerts ({{insertSecurityAlertCount}} new, {{updatedSecurityAlertCount}} updated).",
      }),
    };
  }

  if (isComponentsAndLicenses) {
    return {
      title: t(isPartial
        ? "preferences.import-components-and-licenses-partial-success"
        : "preferences.import-components-and-licenses-success"),
      body: t("preferences.import-count-components-licenses", {
        processedComponentCount: importCount?.component || 0,
        insertComponentCount: insertedCount?.component || 0,
        updatedComponentCount: updatedCount?.component || 0,
        processedLicenseCount: importCount?.license || 0,
        insertLicenseCount: insertedCount?.license || 0,
        updatedLicenseCount: updatedCount?.license || 0,
      }),
    };
  }

  return {
    title: t(isPartial
      ? "preferences.import-components-and-licenses-partial-success"
      : "preferences.import-components-and-licenses-success"),
    body: "",
  };
};

// The GENESIS_IMPORT SSE failure payload puts the EventEntity value in `entity`. Only the CVE
// stream gets its own title; every other stream (and the v1 combined ["components", "licenses"])
// keeps the generic components/licenses title.
const getGenesisImportErrorTitle = (t, entities) => {
  const isCveSync = entities.length === 1 && entities[0] === "cves";
  return t(isCveSync
    ? "preferences.genesis-sync-cves-error"
    : "preferences.import-components-and-licenses-error");
};

const MainLayout = () => {
  const [showUpdateSummary, setShowUpdateSummary] = useState(false);
  const { clearPermissions, hasSuperUserPermission } = useContext(PermissionContext);
  const { syncEntityByKey, refreshStoredSyncMetadata, isSyncingAllData, reconcile } = useAutosyncAllDataAndStore();
  const { data: updateSummary, isLoading, acknowledgeUpdateSummary } =
    useUpdateSummary(hasSuperUserPermission);
  const { t } = useTranslation();
  const isHandlingAuthorizationChange = useRef(false);

  const clearPermissionsRef = useRef(clearPermissions);
  const hasSuperUserPermissionRef = useRef(hasSuperUserPermission);
  const syncEntityByKeyRef = useRef(syncEntityByKey);
  const refreshStoredSyncMetadataRef = useRef(refreshStoredSyncMetadata);
  const reconcileRef = useRef(reconcile);
  const tRef = useRef(t);

  useEffect(() => {
    clearPermissionsRef.current = clearPermissions;
    hasSuperUserPermissionRef.current = hasSuperUserPermission;
    syncEntityByKeyRef.current = syncEntityByKey;
    refreshStoredSyncMetadataRef.current = refreshStoredSyncMetadata;
    reconcileRef.current = reconcile;
    tRef.current = t;
  }, [clearPermissions, hasSuperUserPermission, syncEntityByKey, refreshStoredSyncMetadata, reconcile, t]);

  const handleSseEvent = useCallback(async (eventType, data) => {
    if (eventType === "CONNECT" || eventType === "HEARTBEAT") {
      return;
    }

    if (eventType === "CREATE") {
      await updateDataInIndexedDb(data.entity, data.data, data.id, data.userIds);
      dispatchDataSyncComplete();
    } else if (eventType === "UPDATE") {
      if (data.entity === "preferences") {
        updateLocalPreferences(data.data);
      } else {
        await updateDataInIndexedDb(data.entity, data.data, data.id, data.userIds);
        dispatchDataSyncComplete();
      }
    } else if (eventType === "DELETE") {
      if (data.entity === STORES.PROJECTS && data.data?.cascade) {
        await deleteProjectCascadeInIndexedDb(data.id, data.data.cascade, data.userIds);
        dispatchDataSyncComplete();
      } else {
        await deleteDataInIndexedDb(data.entity, data.id, data.userIds);
        dispatchDataSyncComplete();
      }
    } else if (eventType === "UPDATE_ALL") {
      if (data.entity && Array.isArray(data.entity)) {
        const validStoreValues = new Set(Object.values(STORES));
        let syncedAny = false;
        for (const entityKey of data.entity) {
          if (validStoreValues.has(entityKey) && entityKey !== STORES.SYNC_META) {
            await syncEntityByKeyRef.current(entityKey);
            syncedAny = true;
          }
        }
        if (syncedAny) {
          await refreshStoredSyncMetadataRef.current();
        }
      }
    } else if (eventType === "FOSSID_IMPORT") {
      if (data.userIds.includes(getLocalStorage(LOCAL_STORAGE.USER_ID))) {
        if (data?.data?.code === "success") {
          await syncEntityByKeyRef.current(data.entity);
          await refreshStoredSyncMetadataRef.current();
          const projectCount = data?.data?.data || 0;
          if (hasSuperUserPermissionRef.current) {
            showSuccessToast(tRef.current("preferences.import-projects-success", { count: projectCount }));
          }
        } else if (data?.data?.code === "error") {
          const errorMessage = data?.data?.message || "";
          if (hasSuperUserPermissionRef.current) {
            showErrorToast(tRef.current("preferences.import-projects-error"), errorMessage);
          }
        }
      }
    } else if (eventType === "GENESIS_IMPORT") {
      const genesisEntities = normalizeGenesisSseEntities(data.entity);

      if (data?.data?.code === "SUCCESS") {
        const response = data?.data?.data;
        switch (response) {
          case "Missing Genesis API configuration":
            showErrorToast(tRef.current("status.error"), tRef.current("preferences.missing-genesis-api-configuration"));
            break;
          case "No data return":
            showErrorToast(tRef.current("status.error"), tRef.current("preferences.no-data-return"));
            break;
          default: {
            const { importCount, updatedCount, insertedCount } = response || {};
            if (hasSuperUserPermissionRef.current) {
              const toast = getGenesisImportToastContent(
                t, genesisEntities, importCount, insertedCount, updatedCount, false
              );
              showSuccessToast(toast.title, toast.body);
            }
            dispatchGenesisImportComplete(genesisEntities);
            break;
          }
        }
      } else if (data?.data?.code === "PARTIAL_SUCCESS") {
        if (data.entity && Array.isArray(data.entity)) {
          const validStoreValues = new Set(Object.values(STORES));
          let syncedAny = false;
          for (const entityKey of data.entity) {
            if (validStoreValues.has(entityKey) && entityKey !== STORES.SYNC_META) {
              await syncEntityByKeyRef.current(entityKey);
              syncedAny = true;
            }
          }
          if (syncedAny) {
            await refreshStoredSyncMetadataRef.current();
          }
        }
        const response = data?.data?.data;
        if (response && typeof response === "object") {
          const { importCount, updatedCount, insertedCount } = response;
          if (hasSuperUserPermissionRef.current) {
            const toast = getGenesisImportToastContent(
              t, genesisEntities, importCount, insertedCount, updatedCount, true
            );
            showWarningToast(toast.title, toast.body);
          }
        } else {
          const errorMessage = data?.data?.message || "";
          if (hasSuperUserPermissionRef.current) {
            showWarningToast(
              tRef.current("preferences.import-components-and-licenses-partial-success"),
              errorMessage,
            );
          }
        }
        dispatchGenesisImportComplete(genesisEntities);
      } else if (data?.data?.code === "error") {
        console.log("Genesis sync error", data);
        const errorMessage = data?.data?.message || "";
        if (hasSuperUserPermissionRef.current) {
          switch (errorMessage) {
            case "Missing Genesis API configuration":
              showErrorToast(tRef.current("status.error"), tRef.current("preferences.missing-genesis-api-configuration"));
              break;
            case "No data return":
              showErrorToast(tRef.current("status.error"), tRef.current("preferences.no-data-return"));
              break;
            default:
              showErrorToast(t("status.error"), getGenesisImportErrorTitle(t, genesisEntities), errorMessage);
              break;
          }
        }
        dispatchGenesisImportComplete(genesisEntities);
      }
    } else if (eventType === "ACCOUNT_INACTIVE") {
      const currentUserId = getLocalStorage(LOCAL_STORAGE.USER_ID);
      const targetUserId = data?.data?.userId;
      if (!targetUserId || String(targetUserId) !== String(currentUserId)) {
        return;
      }
      if (!isHandlingAuthorizationChange.current) {
        isHandlingAuthorizationChange.current = true;
        await forceLogoutByAccountInactive(clearPermissionsRef.current);
      }
    } else if (eventType === 'PASSWORD_RESET') {
      const currentUserId = getLocalStorage(LOCAL_STORAGE.USER_ID);
      const targetUserId = data?.data?.userId;
      if (!targetUserId || String(targetUserId) !== String(currentUserId)) {
         return;
      }
      if (!isHandlingAuthorizationChange.current) {
         isHandlingAuthorizationChange.current = true;
         await forceLogoutByPasswordReset(clearPermissions);
      }
    } else if (eventType === "AUTHORIZATION_CHANGED") {
      const currentUserId = getLocalStorage(LOCAL_STORAGE.USER_ID);
      const targetUserId = data?.data?.userId;
      if (!targetUserId || String(targetUserId) !== String(currentUserId)) {
        return;
      }

      const payload = data?.data || {};
      const latestPermissions = payload.permissions || [];
      const storedRawPermissions = localStorage.getItem(LOCAL_STORAGE.PERMISSIONS);
      let storedPermissions = null;

      try {
        storedPermissions = storedRawPermissions ? JSON.parse(storedRawPermissions) : null;
      } catch {
        storedPermissions = null;
      }

      if (!Array.isArray(storedPermissions)) {
        localStorage.setItem(LOCAL_STORAGE.PERMISSIONS, JSON.stringify(latestPermissions));
        return;
      }

      if (!arePermissionsEqual(storedPermissions, latestPermissions) && !isHandlingAuthorizationChange.current) {
        isHandlingAuthorizationChange.current = true;
        await forceLogoutByAuthorizationChange(clearPermissionsRef.current);
      }
    }
  }, []);

  useEffect(() => {
    const eventSource = connectSSE(handleSseEvent, {
      onReconnect: () => {
        reconcileRef.current?.({ force: true });
      },
    });

    return () => {
      disconnectSSE(eventSource);
    };
  }, [handleSseEvent]);

  useCrossTabAuth(clearPermissions);
  useAuthorizationWatcher(clearPermissions);

  useInactivityTimer(3600000, true);

  useEffect(() => {
    if (hasSuperUserPermission && updateSummary && !isLoading) {
      setShowUpdateSummary(true);
    } else if (!hasSuperUserPermission) {
      setShowUpdateSummary(false);
    }
  }, [hasSuperUserPermission, updateSummary, isLoading]);

  const handleCloseUpdateSummary = () => {
    setShowUpdateSummary(false);
    acknowledgeUpdateSummary();
  };

  return (
    <div
      className="flex h-screen min-h-screen bg-[#191B1F] min-w-[1268px]"
      data-testid="main-layout"
    >
      <Sidebar />
      {/* Content area - scrollable with proper constraints */}
      <div className="flex flex-1 flex-col min-h-0 overflow-y-auto px-8 pb-4">
        <Header />
        <main className="mt-6 flex flex-1 flex-col">
          <Outlet />
        </main>
      </div>

      <UpdateSummaryModal
        isOpen={showUpdateSummary}
        onClose={handleCloseUpdateSummary}
        updateSummary={updateSummary}
      />

      <FullScreenDotLoader
        loading={isSyncingAllData}
        text={t("common.syncing-all-data")}
        size={80}
        speedMultiplier={1.2}
      />
    </div>
  );
};

export default MainLayout;
